<template>
  <div class="gang-sheet-builder-button">
    <button
      class="gang-sheet-builder-button__button"
      type="button"
      @click="showBuilder"
      @click.stop
    >
      Build Your Gang Sheet
    </button>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { isProduction } from "common/utils/env";
import { readCookie } from "common/utils/cookie";
import { api } from "jiffy-api";
import { useDtfStore } from "../store";

export default {
  name: "GangSheetBuilderButton",
  data() {
    return {
      inchesToFeet: 12
    };
  },
  mounted() {
    if (this.preloadedDesignId) {
      this.editGangSheet();
      this.setPreloadedDesignId(null);
    }
  },
  computed: {
    ...mapState(useDtfStore, [
      "gangSheetBuilderSettings",
      "gangSheetBuilder",
      "gangSheetBuilderShopId",
      "gangSheetBuilderVariants",
      "gangSheetBuilderSizes",
      "gangSheetBulkPrice",
      "preloadedDesignId",
      "catalogNumber"
    ]),
    builderPayload() {
      const payload = {
        container: "gs-builder", // optional - id of the element for the modal to be rendered in.
        mode: isProduction ? "production" : "development",
        shop_id: this.gangSheetBuilderShopId,
        size_id: this.selectedSize.id,
        sizes: this.gangSheetBuilderSizesAndVariants,
        settings: {
          nameAndNumber: {
            enabled: true
          }
        }
      };

      const customerId = readCookie("gangsheet_builder_customer_id");

      if (customerId) {
        payload.customer = { id: customerId };
      }

      return payload;
    },
    gangSheetBuilderSizesAndVariants() {
      const gangSheetBuilderSizesAndVariants = [];
      let sizeId = 1; // They don't accept 0 as an id

      Object.entries(this.gangSheetBuilderVariants).forEach(([label, variant]) => {
        this.gangSheetBuilderSizes.forEach(size => {
          gangSheetBuilderSizesAndVariants.push({
            ...size,
            title: `${label} - ${size.height / this.inchesToFeet} Feet`,
            metadata: { variantId: variant.variant_id },
            id: sizeId
          });

          sizeId += 1;
        });
      });

      return gangSheetBuilderSizesAndVariants;
    },
    selectedSize() {
      return this.gangSheetBuilderSizesAndVariants.find(
        size =>
          size.height === this.gangSheetBuilderSettings.height &&
          size.metadata.variantId === this.gangSheetBuilderSettings.variantId
      );
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["setLoadedArtwork", "setPreloadedDesignId"]),
    ...mapActions(useDtfStore, ["submitForm"]),
    showBuilder() {
      submitDtfEvent("gangsheet_builder_opened", {
        size: Math.ceil(this.gangSheetBuilderSettings.height / 12.0),
        variant: this.gangSheetBuilderSettings.variantId
      });

      this.gangSheetBuilder
        .open(this.builderPayload)
        .on("design:created", async gangSheetData => {
          const { designs } = gangSheetData.data;

          await this.addToCartGangSheet(designs);
        })
        .on("closed", () => {});
    },
    editGangSheet() {
      this.gangSheetBuilder
        .open({
          ...this.builderPayload,
          shop_id: this.gangSheetBuilderShopId,
          design_id: this.preloadedDesignId
        })
        .on("design:updated", async gangSheetData => {
          const { designs } = gangSheetData.data;

          designs.forEach(design => {
            if (!design.size.metadata)
              design.size.metadata = { variantId: this.gangSheetBuilderSettings.variantId };
          });

          await this.addToCartGangSheet(designs);
        });
    },
    async addToCartGangSheet(designs) {
      const response = await api.dtf.addToCartGangSheet({
        gang_sheet_data: designs,
        catalog_number: this.catalogNumber
      });

      this.gangSheetBuilder.close();

      response.artworks.forEach((artwork, index) => {
        const bulkPrice = this.gangSheetBulkPrice[artwork.variant_id]?.bulk_price;
        let gangsheetPrice = bulkPrice?.find(
          size => size.height === artwork.height * artwork.quantity
        );
        const selectedVariant = Object.values(this.gangSheetBuilderVariants).find(
          variant => variant.variant_id === artwork.variant_id
        );
        gangsheetPrice ||= bulkPrice[bulkPrice.length - 1];

        artwork.pricing = {
          unitPrice: gangsheetPrice.size_price * Math.ceil(artwork.height / 12.0)
        };
        artwork.catalogNumber = selectedVariant.catalog_number;
        artwork.fromGangSheetBuilder = true;

        this.setLoadedArtwork({
          artwork,
          index
        });
      });
      this.submitForm();
    }
  }
};
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";

.gang-sheet-builder-button {
  margin: 20px 0;

  &__button {
    background-color: $dropzone-highlight;
    font-weight: 700;
    width: 100%;
    color: $white;
    border-radius: 8px;
    padding: 10px 16px;
    border: none;
  }
}
</style>
